import {
  createTokenRefreshInterceptor,
  getMarketingNotifications,
  type RequestService,
  transformKeysToCamelCase,
} from '@metapic-apps/shared'
import axios, { type AxiosError, type AxiosInstance } from 'axios'

import { config } from '@/lib/helpers'
import { notifyUserBanned } from '@/lib/services/banned-user.service'
import { setNotificationsUnavailable } from '@/lib/services/notification-availability.service'
import { useAuthStore } from '@/stores/auth.stores'

type ClientOptions = {
  shouldTransformKeysToCamelCase?: boolean
  withInterceptors?: boolean
  trackAvailability?: boolean
}

const createApiClient = (baseURL: string, options: ClientOptions): AxiosInstance => {
  const apiClient = axios.create({
    baseURL,
  })

  if (options.withInterceptors) {
    apiClient.interceptors.request.use(
      (request) => {
        const authStore = useAuthStore()

        request.headers.setAuthorization(authStore.authorization || '')
        request.headers.setContentType(
          request.isFormData ? 'multipart/form-data' : 'application/json',
        )

        return request
      },
      (error: AxiosError) => Promise.reject(error),
    )

    apiClient.interceptors.response.use(
      (response) => {
        if (
          response.config.shouldTransformKeysToCamelCase ??
          options?.shouldTransformKeysToCamelCase
        ) {
          return {
            ...response,
            data: transformKeysToCamelCase(response.data),
          }
        }
        return response
      },
      (
        error: AxiosError<{
          code?: string
          message?: string
        }>,
      ) => {
        if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
          notifyUserBanned()
        }

        return Promise.reject(error)
      },
    )

    createTokenRefreshInterceptor(apiClient, {
      refreshToken: () => useAuthStore().refreshToken(),
      onRefreshFailure: () => useAuthStore().handleRefreshFailure(),
    })
  }

  if (options.trackAvailability) {
    apiClient.interceptors.response.use(
      (response) => {
        setNotificationsUnavailable(false)
        return response
      },
      (error: AxiosError) => {
        const isNetworkError = !error.response
        const isServerError = (error.response?.status ?? 0) >= 500
        if (isNetworkError || isServerError) {
          setNotificationsUnavailable(true)
        }
        return Promise.reject(error)
      },
    )
  }

  return apiClient
}

const createRequestService = <FormatCamelCaseByDefault extends boolean = false>(
  apiClient: AxiosInstance,
): RequestService<FormatCamelCaseByDefault> => ({
  get: (resource, config) => apiClient.get(resource, config),
  getBlob: (resource, config) => {
    const updatedConfig = { ...(config || {}), responseType: 'blob' as const }
    return apiClient.get(resource, updatedConfig)
  },
  post: (resource, data, config) => apiClient.post(resource, data, config),
  put: (resource, data, config) => apiClient.put(resource, data, config),
  delete: (resource, config) => apiClient.delete(resource, config),
  patch: (resource, data, config) => apiClient.patch(resource, data, config),
})

export const request = createRequestService(
  createApiClient(config.metapicApiUrl, { withInterceptors: true }),
)
const notificationAxios = createApiClient(config.notificationApiUrl, {
  withInterceptors: true,
  trackAvailability: true,
})
export const notificationRequest = createRequestService(notificationAxios)
export const marketingNotificationsClient = getMarketingNotifications(notificationAxios)
export const externalApiRequest = createRequestService(
  createApiClient('', { withInterceptors: false }),
)
